import { Grid, IconButton } from "@mui/material";
import { EnsolversOpsUser, WelcomeFormInfo } from "../../models/EnsolversOpsUser";
import { AppListItem } from "../AppListItem";
import PortraitOutlinedIcon from "@mui/icons-material/PortraitOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AssistantPhotoOutlinedIcon from "@mui/icons-material/AssistantPhotoOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import GitHubIcon from "@mui/icons-material/GitHub";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import OpsDateUtils from "../../utils/OpsDateUtils";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SchoolIcon from "@mui/icons-material/School";
import PetsIcon from "@mui/icons-material/Pets";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import ExtensionIcon from "@mui/icons-material/Extension";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import GenericForm from "../../external/ensolvers-core-frontend-mui/components/Common/GenericForm";
import { SpinnerWrapper } from "../SpinnerWrapper";
import { UserUpdateModal } from "../UserProfile/UserUpdateModal";
import { useContext } from "react";
import { PersonalInformationContext } from "../../routes/PersonalInfo";
import featureToggleList from "../../utils/toggleConfig.json";
import { Features } from "../../utils/Features";
import { useFeatureToggle } from "../../external/ensolvers-core-frontend-mui/hooks/useFeatureToggle";
import OpsAvatarComponent from "../OpsAvatarComponent";
import { ButtonComponent } from "../ButtonComponent";
import { UserAvatar } from "../UserProfile/UserAvatar";
import OpsPanel from "../OpsPanel";
import userIcon from "../../img/icons/user_icon.svg";
import factCheck from "../../img/icons/fact_check.svg";
import { useStyles } from "../Main/formStyles";
import { UsePersonalInfoReturnType } from "../../hooks/usePersonalInfo";
import NotificationService from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { Token } from "../../theme";

export const PersonalData = () => {
  const {
    user,
    isLoading,
    isRequestPending,
    canEdit,
    userUpdate,
    setUserUpdate,
    userAvatarUpdate,
    onSave,
    clearUserUpdate,
    showAdditionalVacationAmount,
    setIsLoading,
    setUser,
    setUserAvatarUpdate,
  } = useContext<UsePersonalInfoReturnType>(PersonalInformationContext);
  const welcomeFormInfo: WelcomeFormInfo = user.welcomeFormInfo ?? {};
  const [isVacationReportEnabled] = useFeatureToggle(featureToggleList, Features.VACATION_REPORT);
  const disabledDateLabel =
    user?.enabled === false && user?.disabledAt ? `Disabled on ${OpsDateUtils.formatInUTCAsDDMMMYYY(user.disabledAt)}` : undefined;

  const customStyles = useStyles();

  const handleCopy = async (text: string | undefined, label: string) => {
    try {
      if (text) {
        await navigator.clipboard.writeText(text);
        NotificationService.info(`Copied ${label} to Clipboard!`);
      } else {
        throw new Error(`No ${label} available to copy`);
      }
    } catch (err) {
      const message = err.message || "Unknown Error";
      NotificationService.error(message);
    }
  };

  const CopyButton = ({ text, label }: { text?: string; label: string }) => (
    <IconButton size="small" disabled={!text || text === "-"} onClick={() => handleCopy(text, label)} sx={{ mr: 0, p: 0.5 }}>
      <ContentCopyIcon fontSize="small" color={text ? "primary" : "disabled"} />
    </IconButton>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <OpsPanel
        title="Personal data"
        svg={userIcon}
        topBannerLabel={user?.enabled === false ? "Inactive user" : undefined}
        topBannerRightLabel={disabledDateLabel}
      >
        <Grid container justifyContent={"flex-start"}>
          <Grid item xs={12} md={4}>
            <AppListItem primaryText={`First Name`} secondaryText={`${user?.firstName}`} />
            <Grid container alignItems="center" wrap="nowrap">
              <AppListItem primaryText={`Email`} secondaryText={`${user?.email}`} />
              <CopyButton text={user?.email} label="Email" />
            </Grid>
            <AppListItem
              primaryText={`Country, Region`}
              secondaryText={(user?.country ? `${user.country}` : "-") + (user?.region ? `, ${user.region}` : "")}
            />
            {showAdditionalVacationAmount() && (
              <AppListItem
                primaryText={`Vacations Computed Since`}
                secondaryText={`${OpsDateUtils.ensureFormatDateUTC(user?.vacationsComputedSince)}`}
              />
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <AppListItem primaryText={`Last Name`} secondaryText={`${user?.lastName}`} />
            <AppListItem primaryText={`Phone`} secondaryText={`${user?.phone || "-"}`} />
            <Grid container alignItems="center" wrap="nowrap">
              <AppListItem primaryText={`Github Username`} secondaryText={`${user?.githubUsername || "-"}`} />
              <CopyButton text={user?.githubUsername} label="Github Username" />
            </Grid>
            {isVacationReportEnabled && user?.vacationAccumulation !== 10 && (
              <AppListItem primaryText={`Vacation Accumulation per year (days)`} secondaryText={`${user?.vacationAccumulation || "-"}`} />
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <AppListItem
              primaryText={`Birthday`}
              secondaryText={user?.shortBirthday ? `${OpsDateUtils.formatUTCAsDDMMM(user?.shortBirthday)}` : "-"}
            />
            <AppListItem primaryText={`Joined`} secondaryText={`${OpsDateUtils.ensureFormatDateUTC(user?.shortStart)}`} />
            <Grid container alignItems="center" wrap="nowrap">
              <AppListItem primaryText={`Slack User Id`} secondaryText={`${user?.slackUserId || "-"}`} />
              <CopyButton text={user?.slackUserId} label="Slack ID" />
            </Grid>
          </Grid>
        </Grid>
      </OpsPanel>

      <OpsPanel title="Welcome Form Info" svg={factCheck}>
        <Grid container alignItems={"center"} justifyContent={"space-evenly"} spacing={2}>
          <Grid item xs={12} md={4}>
            <AppListItem primaryText={`Student`} secondaryText={`${welcomeFormInfo?.student ? "Yes" : "No"}`} />
            <AppListItem primaryText={`Favourite Game`} secondaryText={`${welcomeFormInfo?.favouriteGame || "-"}`} />
          </Grid>
          <Grid item xs={12} md={4}>
            <AppListItem primaryText={`Pets`} secondaryText={`${welcomeFormInfo?.pets || "-"}`} />
            <AppListItem primaryText={`Favourite Genre/Band`} secondaryText={`${welcomeFormInfo?.musicPreferences || "-"}`} />
          </Grid>
          <Grid item xs={12} md={4}>
            <AppListItem primaryText={`Hobbies`} secondaryText={`${welcomeFormInfo?.hobbies || "-"}`} />
            <AppListItem primaryText={`Personal Description`} secondaryText={`${welcomeFormInfo?.wordDescription || "-"}`} />
          </Grid>
        </Grid>
      </OpsPanel>

      <UserAvatar
        userAvatarUpdate={userAvatarUpdate}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setUserAvatarUpdate={setUserAvatarUpdate}
        setUser={setUser}
      />

      {userUpdate && (
        <GenericForm<EnsolversOpsUser>
          dialog={true}
          open={true}
          disableMultipleSubmit
          instance={user}
          title={isLoading ? "" : "Edit"}
          onSave={onSave}
          onCancel={clearUserUpdate}
          renderContent={(register, errors, control) => (
            <SpinnerWrapper isLoading={isRequestPending}>
              <UserUpdateModal
                isVacationReportEnabled={isVacationReportEnabled}
                control={control}
                register={register}
                errors={errors}
                canEdit={canEdit}
              />
            </SpinnerWrapper>
          )}
          validateBeforeClosing
          headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
          buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
          cancelButtonStyles={{
            color: Token.textSecondary,
            border: `1px solid ${Token.borderDefault}`,
            marginBottom: 10,
          }}
          dialogStyles={customStyles.root}
          forceFormOrientationCol={true}
        />
      )}
    </div>
  );
};
